/**
 * BYO Performance Settings panel.
 *
 * Shows every Ray knob that is meaningful for a BYO batch job, with its
 * current value (or the system default when the user hasn't changed it),
 * a unit label, and a tooltip explaining what it does and when to tune it.
 *
 * Wiring status per knob (nexla-ray reference):
 *   max_concurrency  — wired (runtime_config.max_concurrency, needs server-cap gate)
 *   cpu / memory     — planned (Ray num_cpus / memory per worker task)
 *   timeout          — planned (max_wait_seconds)
 *   retries          — planned (max_item_requeues)
 *
 * All values are stored in the draft and serialized to code_config.performance_config
 * so the backend can start consuming them as each knob is wired server-side.
 */

import React from 'react';
import { Box, TextField, Tooltip, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import type { BYOPerformanceConfig } from 'new/components/flows/types/byo/core/model';
import classes from './studio.module.scss';

// ── Field metadata ────────────────────────────────────────────────────────────

interface FieldMeta {
  key: keyof BYOPerformanceConfig;
  label: string;
  unit: string;
  tooltip: string;
  min: number;
  max?: number;
  /** Field is plan-controlled and cannot be edited by the user. */
  locked?: boolean;
  /** Field is not yet wired to the backend — shown for visibility only. */
  pendingWiring?: boolean;
}

const FIELDS: FieldMeta[] = [
  {
    key: 'cpu',
    label: 'CPU',
    unit: 'vCPU',
    tooltip:
      'vCPUs allocated to each file-processing worker. Increase for CPU-bound transformations (e.g. heavy pandas / numpy work). Allowed: 1–8. Default: 2.',
    min: 1,
    max: 8,
    pendingWiring: true,
  },
  {
    key: 'memory',
    label: 'Memory',
    unit: 'GB',
    tooltip:
      'RAM allocated to each worker. Increase if your code loads large files or memory-intensive packages. Allowed: 1–16 GB. Default: 2 GB.',
    min: 1,
    max: 16,
    pendingWiring: true,
  },
  {
    key: 'timeout',
    label: 'Timeout',
    unit: 'sec',
    tooltip:
      'How long the job waits for new files before shutting down (Ray: max_wait_seconds). Increase for slow or intermittent sources. Allowed: 30–3600 s. Default: 300 s.',
    min: 30,
    max: 3600,
    pendingWiring: true,
  },
  {
    key: 'retries',
    label: 'Retries',
    unit: '',
    tooltip:
      'Times a failed file is re-queued before being permanently marked as failed (Ray: max_item_requeues). Set to 0 to fail fast. Allowed: 0–5. Default: 3.',
    min: 0,
    max: 5,
    pendingWiring: true,
  },
];

const CONCURRENCY_META: FieldMeta = {
  key: 'concurrency',
  label: 'Concurrency',
  unit: '',
  tooltip:
    'Maximum files processed in parallel (Ray: runtime_config.max_concurrency). Higher = faster throughput but more compute cost. Controlled by your plan — contact your admin to increase. Default: 4.',
  min: 1,
  locked: true,
};

// ── Component ─────────────────────────────────────────────────────────────────

export interface PerformanceConfigPanelProps {
  value: BYOPerformanceConfig;
  onChange: (next: BYOPerformanceConfig) => void;
  'data-testid'?: string;
}

function FieldLabel({ meta }: { meta: FieldMeta }) {
  return (
    <Box className={classes.perfLabelRow}>
      <span
        className={[classes.perfLabel, meta.locked ? classes.perfLabelLocked : '']
          .filter(Boolean)
          .join(' ')}
      >
        {meta.label}
        {meta.unit ? <span className={classes.perfUnit}> ({meta.unit})</span> : null}
      </span>
      {meta.locked && <LockOutlinedIcon className={classes.perfLockIcon} />}
      <Tooltip title={meta.tooltip} placement="top" arrow>
        <HelpOutlineIcon className={classes.perfInfoIcon} />
      </Tooltip>
    </Box>
  );
}

export function PerformanceConfigPanel({
  value,
  onChange,
  'data-testid': dataTestId = 'byo-perf-config',
}: PerformanceConfigPanelProps) {
  // Transient per-field edit buffer: while a field is focused we let the user
  // type freely (including clearing it entirely) and only clamp + commit on
  // blur. Clamping on every keystroke made it impossible to delete a value and
  // type a custom one — the field snapped back to `min` the moment it emptied.
  const [editing, setEditing] = React.useState<Partial<Record<keyof BYOPerformanceConfig, string>>>(
    {},
  );

  const commit = (key: keyof BYOPerformanceConfig, raw: string) => {
    setEditing(({ [key]: _drop, ...rest }) => rest);
    const meta = [...FIELDS, CONCURRENCY_META].find((f) => f.key === key)!;
    const n = Number(raw);
    // Empty / invalid input reverts to the previous value rather than snapping.
    const next =
      raw.trim() === '' || !Number.isFinite(n)
        ? value[key]
        : Math.max(meta.min, meta.max != null ? Math.min(meta.max, n) : n);
    if (next !== value[key]) onChange({ ...value, [key]: next });
  };

  return (
    <Box className={classes.perfPanel} data-testid={dataTestId}>
      <Typography className={classes.perfTitle}>Performance Settings</Typography>
      <Typography className={classes.perfSubtitle}>
        Ray job resource allocation for this flow. Defaults shown — tune for your workload.
      </Typography>

      {/* 2-column grid: CPU | Memory, Timeout | Retries */}
      <Box className={classes.perfGrid}>
        {FIELDS.map((f) => {
          // Validate the live buffer so out-of-range input shows an error before
          // blur clamps it. Values are always clamped on commit, so the payload
          // can never exceed the allowed range — this just makes the limit visible.
          const raw = editing[f.key] ?? String(value[f.key]);
          const n = Number(raw);
          const invalid =
            raw.trim() === '' || !Number.isFinite(n) || n < f.min || (f.max != null && n > f.max);
          const rangeHint = f.max != null ? `${f.min}–${f.max}` : `≥ ${f.min}`;
          return (
            <Box key={f.key} className={classes.perfField}>
              <FieldLabel meta={f} />
              <TextField
                size="small"
                type="number"
                value={raw}
                error={invalid}
                helperText={invalid ? `Enter ${rangeHint}${f.unit ? ` ${f.unit}` : ''}` : undefined}
                inputProps={{ min: f.min, max: f.max, 'data-testid': `${dataTestId}-${f.key}` }}
                onChange={(e) => setEditing((prev) => ({ ...prev, [f.key]: e.target.value }))}
                onBlur={(e) => commit(f.key, e.target.value)}
              />
            </Box>
          );
        })}
      </Box>

      {/* Concurrency — plan-locked, full width */}
      <Box className={classes.perfFieldFull}>
        <FieldLabel meta={CONCURRENCY_META} />
        <TextField
          size="small"
          type="number"
          value={value.concurrency}
          disabled
          inputProps={{ 'data-testid': `${dataTestId}-concurrency` }}
        />
      </Box>

      <Typography className={classes.perfNote}>
        CPU, Memory, Timeout and Retries are saved and will take effect once server-side wiring
        lands. Concurrency is plan-controlled.
      </Typography>
    </Box>
  );
}

export default PerformanceConfigPanel;
